import { ColumnDef } from '@tanstack/react-table';
import {
  ColumnMeta,
  Entity,
  FilterResultMethod,
  mapEntity,
} from '@toggle/toggle';
import { TFunction } from 'i18next';
import React from 'react';

import { OneDayReturnCell } from '~/components/one-day-return-cell/OneDayReturnCell';
import { LIVE_ONE_DAY_RETURN_COLUMN_META_VALUE_FIELD } from '~/views/screener/results/constants';
import { ScreenerCell } from '~/views/screener/screener-cell/ScreenerCell';
import { CompetingThemesCell } from '~/widgets/knowledge-graph-widget/components/competitors-tab/competing-themes-cell/CompetingThemesCell';
import { CompetitorsTableData } from '~/widgets/knowledge-graph-widget/hooks/useCompetitorsData';

import { TopRankIcon } from '../../top-rank-icon/TopRankIcon';
import { CompanyCell } from '../company-cell/CompanyCell';
import * as S from './CompetitorsTable.styles';

const TABLE_FIELDS = {
  RANK: 'rank',
  COMPANY: 'tag',
  THEMES: 'themes',
  LIVE_ONE_DAY_RETURN: LIVE_ONE_DAY_RETURN_COLUMN_META_VALUE_FIELD,
  MARKET_CAP: 'market_cap.value',
};

export const getColumns = ({
  t,
  columnsMeta,
}: {
  t: TFunction;
  columnsMeta: ColumnMeta[];
}) => {
  const columns: ColumnDef<CompetitorsTableData>[] = [
    {
      id: TABLE_FIELDS.RANK,
      header: t('widget:assetOverview.knowledgeGraph.competitors.columns.rank'),
      cell: props => {
        const { rank } = props.row.original;

        return (
          <S.RankWrapper>
            <TopRankIcon rank={rank} />
          </S.RankWrapper>
        );
      },
    },
    {
      id: TABLE_FIELDS.COMPANY,
      header: t(
        'widget:assetOverview.knowledgeGraph.competitors.columns.company'
      ),
      cell: props => {
        const entity = props.row.original.entity;

        if (!entity) {
          return null;
        }

        return <CompanyCell entity={entity} />;
      },
    },
    {
      id: TABLE_FIELDS.THEMES,
      header: t(
        'widget:assetOverview.knowledgeGraph.competitors.columns.competingThemes'
      ),
      cell: props => {
        const themes = props.row.original.competingThemes.map(t => t.name);
        return <CompetingThemesCell themes={themes} />;
      },
    },
    {
      id: TABLE_FIELDS.LIVE_ONE_DAY_RETURN,
      header: columnsMeta.find(
        c => c.field === TABLE_FIELDS.LIVE_ONE_DAY_RETURN
      )?.name,
      accessorFn: row =>
        row[TABLE_FIELDS.LIVE_ONE_DAY_RETURN as keyof CompetitorsTableData],
      cell: props => {
        const entityData = props.row.original.entity as Entity | undefined;

        return (
          <OneDayReturnCell
            asset={entityData ? mapEntity(entityData) : undefined}
            showPercentageSuffix
          />
        );
      },
    },
    {
      id: TABLE_FIELDS.MARKET_CAP,
      header: columnsMeta.find(c => c.field === TABLE_FIELDS.MARKET_CAP)?.name,
      accessorFn: row =>
        row[TABLE_FIELDS.MARKET_CAP as keyof CompetitorsTableData],
      cell: ({ getValue }) => {
        const col = columnsMeta.find(c => c.field === TABLE_FIELDS.MARKET_CAP);
        const value = getValue() as FilterResultMethod;

        if (!col) {
          return null;
        }

        return <ScreenerCell value={value} columnMeta={col} />;
      },
    },
  ];

  return columns;
};
